/**
 * Created by razial on 16.02.2026
 */

const EditorClient = function (editor) {
    cleverapps.EventEmitter.call(this);

    this.editor = editor;

    this.commandLog = new EditorCommandLog();

    this.queued = [];
    this.pending = [];
    this.received = [];

    this.undoStack = [];
    this.redoStack = [];

    this.connectionState = EditorClient.CONNECTION_DISCONNECTED;
    this.fileState = EditorClient.FILE_CLOSED;
    this.error = undefined;

    this.file = undefined;

    this.transport = new WebSocketClientTransport({
        url: cleverapps.config.editorApiURL
    });
    this.transport.on("connected", () => this.setConnectionState(EditorClient.CONNECTION_CONNECTED), this);
    this.transport.on("disconnected", () => this.setConnectionState(EditorClient.CONNECTION_DISCONNECTED), this);
    this.transport.on("message", (message) => this.processMessages(message), this);

    cleverapps.git.on("changed", this.processFileState.bind(this), this);

    this.autoPushInterval = cleverapps.timeouts.setInterval(this.autoPush.bind(this), EditorClient.AUTOPUSH_INTERVAL);

    this.processBlockedState();
    this.processFileState();
};

EditorClient.prototype = Object.create(cleverapps.EventEmitter.prototype);
EditorClient.prototype.constructor = EditorClient;

EditorClient.prototype.destructor = function () {
    this.destructed = true;

    cleverapps.timeouts.clearInterval(this.autoPushInterval);

    this.processBlockedState();
    this.disconnect();

    runCleaners(this);
};

EditorClient.prototype.setConnectionState = function (state) {
    if (this.connectionState === state) {
        return;
    }

    console.log("EditorClient connection state", state);

    this.connectionState = state;

    this.processBlockedState();
    this.processFileState();

    this.trigger("updateState");
};

EditorClient.prototype.getConnectionState = function () {
    return this.connectionState;
};

EditorClient.prototype.setFileState = function (state) {
    if (this.fileState === state) {
        return;
    }

    console.log("EditorClient file state", state);

    this.fileState = state;

    this.processBlockedState();
    this.processFileState();

    this.trigger("updateState");
};

EditorClient.prototype.getFileState = function () {
    return this.fileState;
};

EditorClient.prototype.processMessages = function (message) {
    if (message.type !== "pointer") {
        console.log("EditorClient receive message", message);
    }

    if (message.type === "opened") {
        this.setFileState(EditorClient.FILE_OPENED);
    } else if (message.type === "commands") {
        this.received.push(...(message.data || []));
        this.processReceivedCommands();
    } else if (message.type === "editors") {
        this.setEditors(message.data || {});
    } else if (message.type === "nuke") {
        alert(`page was nuked by ${message.data.editor}`);
        cleverapps.git.revert(cleverapps.git.listAllChanges());
        setTimeout(() => {
            connector.platform.locationReload();
        }, 500);
    } else if (message.type) {
        this.trigger("message", message);
    }
};

EditorClient.prototype.getUserId = function () {
    return AdminAccess.userName;
};

EditorClient.prototype.getProjectId = function () {
    return cleverapps.config.name;
};

EditorClient.prototype.connect = function () {
    if (AdminAccess.userName) {
        this.transport.open(AdminAccess.userName);
    }
};

EditorClient.prototype.disconnect = function () {
    this.transport.close();
};

EditorClient.prototype.send = function (type, data) {
    return this.transport.send(type, data);
};

EditorClient.prototype.open = function (filename, commandUid, updater) {
    this.file = {
        filename,
        commandUid,
        updater
    };

    this.processFileState();
};

EditorClient.prototype.processBlockedState = function () {
    if (cleverapps.isLocalhost()) {
        return;
    }

    if (this.error) {
        PointerHandler.blocked = true;
        return;
    }

    if (this.destructed || this.reseted || this.connectionState === EditorClient.CONNECTION_CONNECTED && this.fileState === EditorClient.FILE_OPENED) {
        PointerHandler.blocked = false;
    } else {
        PointerHandler.blocked = true;
    }
};

EditorClient.prototype.processFileState = function () {
    if (this.connectionState !== EditorClient.CONNECTION_CONNECTED) {
        return;
    }

    if (this.fileState === EditorClient.FILE_CLOSED) {
        if (this.file) {
            this.send("open", {
                projectId: this.getProjectId(),
                filename: this.file.filename,
                commandUid: this.file.commandUid
            });
        }
    }
};

EditorClient.prototype.beginBatch = function () {
    this.currentBatchId = Date.now().toString(36);
};

EditorClient.prototype.endBatch = function () {
    this.currentBatchId = undefined;
};

EditorClient.prototype.doCommand = function (type, data) {
    const command = new EditorCommand({
        type, data, batchId: this.currentBatchId, userId: this.getUserId()
    });
    const done = command.execute(this.editor);
    if (!done) {
        cleverapps.throwAsync(`can't execute command ${JSON.stringify(command.getInfo())}`);
        return undefined;
    }

    this.queued.push(command);
    this.undoStack.push(command);
    this.redoStack.length = 0;

    this.file.updater(command.uid);

    const info = command.getInfo();
    this.commandLog.add(info.uid, this.getUserId(), info.type, info.data);

    this.sendCommands();

    this.trigger("updateCommands");

    return command.uid;
};

EditorClient.prototype.undoCommand = function () {
    if (!this.undoStack.length) {
        return;
    }

    const last = this.undoStack[this.undoStack.length - 1];
    const batchId = last.batchId;

    do {
        const index = this.undoStack.length - 1;
        const command = this.undoStack[index];
        const undoCommand = new EditorCommand(command.undoData);
        undoCommand.userId = this.getUserId();

        const done = undoCommand.execute(this.editor);
        if (!done) {
            cleverapps.throwAsync(`can't undo command ${JSON.stringify(undoCommand.getInfo())}`);
            return false;
        }

        undoCommand.batchId = command.batchId;
        undoCommand.originalUid = command.originalUid || command.uid;
        this.undoStack.splice(index, 1);
        this.queued.push(undoCommand);
        this.redoStack.push(undoCommand);

        this.commandLog.markUndone(undoCommand.originalUid);
        this.file.updater(command.uid);
    } while (batchId && this.undoStack.length && this.undoStack[this.undoStack.length - 1].batchId === batchId);

    this.sendCommands();

    this.trigger("updateCommands");

    return true;
};

EditorClient.prototype.redoCommand = function () {
    if (!this.redoStack.length) {
        return;
    }

    const last = this.redoStack[this.redoStack.length - 1];
    const batchId = last.batchId;

    do {
        const index = this.redoStack.length - 1;
        const command = this.redoStack[index];
        const redoCommand = new EditorCommand(command.undoData);
        redoCommand.userId = this.getUserId();

        const done = redoCommand.execute(this.editor);
        if (!done) {
            cleverapps.throwAsync(`can't redo command ${JSON.stringify(redoCommand.getInfo())}`);
            return false;
        }

        redoCommand.batchId = command.batchId;
        redoCommand.originalUid = command.originalUid;
        this.redoStack.splice(index, 1);
        this.queued.push(redoCommand);
        this.undoStack.push(redoCommand);

        this.commandLog.markRedone(command.originalUid);
        this.file.updater(command.uid);
    } while (batchId && this.redoStack.length && this.redoStack[this.redoStack.length - 1].batchId === batchId);

    this.sendCommands();

    this.trigger("updateCommands");

    return true;
};

EditorClient.prototype.resetAllChanges = function () {
    this.reseted = true;

    this.processBlockedState();

    this.send("nuke");

    this.trigger("updateState");
    this.trigger("updateCommands");

    return true;
};

EditorClient.prototype.sendCommands = cleverapps.accumulate(500, function () {
    if (!this.queued.length) {
        return;
    }

    const commands = this.queued.splice(0, this.queued.length);
    this.pending.push(...commands);

    this.send("commands", commands.map((command) => command.getInfo()));
});

EditorClient.prototype.processReceivedCommands = function () {
    const pendingCommands = this.pending;
    const receivedCommands = this.received;

    const processError = (error) => {
        this.error = error;

        alert("Error syncing commands. Please make sure no one is using the editor and only then use the reset button (skull) to fix the error");

        this.processBlockedState();
    };

    while (receivedCommands.length) {
        let index = 0;

        while (index < receivedCommands.length && index < pendingCommands.length && pendingCommands[index].uid === receivedCommands[index].uid) {
            index += 1;
        }

        pendingCommands.splice(0, index);
        receivedCommands.splice(0, index);

        if (!receivedCommands.length) {
            break;
        }

        index = pendingCommands.length - 1;

        while (index >= 0) {
            const undoCommand = new EditorCommand(pendingCommands[index].undoData);
            const done = undoCommand.execute(this.editor);
            if (!done) {
                const error = `can't do command ${JSON.stringify(undoCommand.getInfo())}`;
                processError(error);
                throw error;
            }
            this.file.updater(undoCommand.uid);
            index -= 1;
        }

        index = 0;

        while (index < receivedCommands.length && (!pendingCommands.length || pendingCommands[0].uid !== receivedCommands[index].uid)) {
            const rawCmd = receivedCommands[index];
            const command = new EditorCommand(rawCmd);
            const done = command.execute(this.editor);
            if (!done) {
                const error = `can't do command ${JSON.stringify(command.getInfo())}`;
                processError(error);
                throw error;
            }
            const info = command.getInfo();
            this.commandLog.add(info.uid, rawCmd.userId || "?", info.type, info.data);
            this.file.updater(command.uid);
            index += 1;
        }

        receivedCommands.splice(0, index);

        index = 0;

        while (index < pendingCommands.length) {
            const command = pendingCommands[index];
            const done = command.execute(this.editor);
            if (!done) {
                const error = `can't do command ${JSON.stringify(command.getInfo())}`;
                processError(error);
                throw error;
            }
            this.file.updater(command.uid);
            index += 1;
        }
    }
};

EditorClient.prototype.setEditors = function (data) {
    this.editors = data.editors || [];
    this.isCommitter = data.committer !== undefined && data.committer === this.getUserId();

    this.trigger("updateEditors");
};

EditorClient.prototype.getEditors = function () {
    return this.editors;
};

EditorClient.prototype.autoPush = function () {
    const synced = this.connectionState === EditorClient.CONNECTION_CONNECTED && this.fileState === EditorClient.FILE_OPENED;

    if (this.isCommitter && synced && !this.error) {
        cleverapps.git.autoPush(this.file.filename);
    }
};

EditorClient.AUTOPUSH_INTERVAL = 5 * 60 * 1000;

EditorClient.CONNECTION_CONNECTED = "connected";
EditorClient.CONNECTION_DISCONNECTED = "disconnected";

EditorClient.FILE_CLOSED = "closed";
EditorClient.FILE_OPENED = "opened";

EditorClient.COMMANDS = new Map();

EditorClient.registerCommand = function (name, command) {
    EditorClient.COMMANDS.set(name, command);
};